import { useState } from "react";
import { Link } from "react-router-dom";
import { TurnstileWidget } from "../components/Turnstile.tsx";

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // After a successful submit we show a fixed "if an account exists,
  // we sent a link" message regardless of whether the email was real.
  // Revealing existence here would be an email-enumeration oracle.
  const [submitted, setSubmitted] = useState(false);

  if (submitted) {
    return (
      <div className="center">
        <div className="card compact">
          <h2>Check your email</h2>
          <p className="muted">
            If <strong>{email}</strong> matches an account, a password-reset
            link has been sent. It expires shortly — open the email and click
            the link to set a new password.
          </p>
          <p className="muted mt">
            <Link to="/login">Back to sign in</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="center">
      <div className="card compact">
        <h2>Forgot password</h2>
        <p className="muted">
          Enter the email address on your account. We'll send you a link to
          set a new password.
        </p>
        <form
          className="formstack"
          onSubmit={async (e) => {
            e.preventDefault();
            setErr(null);
            setBusy(true);
            // Better-Auth's React client doesn't expose forgetPassword
            // on its typed surface; calling the underlying endpoint
            // directly gives us full control over headers (Turnstile
            // token) and is a single same-origin POST.
            //
            // redirectTo is the URL the email link points at. After
            // the user clicks it they land at /reset-password with
            // the token in the query string.
            const resp = await fetch("/v1/auth/forget-password", {
              method: "POST",
              credentials: "include",
              headers: {
                "content-type": "application/json",
                "X-Turnstile-Token": turnstileToken,
              },
              body: JSON.stringify({
                email,
                redirectTo: `${window.location.origin}/reset-password`,
              }),
            });
            setBusy(false);
            if (!resp.ok) {
              const body = await resp.json().catch(() => ({} as { message?: string }));
              setErr(body.message ?? `Couldn't send reset email (${resp.status})`);
              return;
            }
            setSubmitted(true);
          }}
        >
          <label>
            Email
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          {TURNSTILE_SITE_KEY && (
            <TurnstileWidget
              siteKey={TURNSTILE_SITE_KEY}
              onVerify={setTurnstileToken}
              onExpired={() => setTurnstileToken("")}
              onError={() => setTurnstileToken("")}
            />
          )}
          {err && <div className="err">{err}</div>}
          <button type="submit" disabled={busy || !turnstileToken}>
            {busy ? "Sending…" : "Send reset link"}
          </button>
        </form>
        <p className="muted mt">
          Remembered it? <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
